import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trophy, Medal, Award } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface Player {
  position: number;
  name: string;
  partner: string;
  wins?: number;
  losses?: number;
  setsFor?: number;
  setsAgainst?: number;
  gamesFor: number;
  gamesAgainst: number;
  difference: number;
  isCurrentUser: boolean;
}

interface LeagueRankingTableProps {
  players: Player[];
  modalidad: 'singles' | 'doubles';
  type: 'round-robin' | 'sumatoria-juegos';
  sport?: 'tennis' | 'padel' | 'pickleball';
  qualifyingSpots?: number;
}

const LeagueRankingTable = ({ players, modalidad, type, sport = 'padel', qualifyingSpots }: LeagueRankingTableProps) => {
  const { t } = useLanguage();
  
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return <Trophy className="h-4 w-4 text-amber-500" />;
      case 2: return <Medal className="h-4 w-4 text-gray-400" />;
      case 3: return <Award className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  const getPositionBadge = (position: number) => {
    if (position <= 3) {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          {t('league.podium')}
        </Badge>
      );
    }
    return null;
  };

  const isMobile = useIsMobile();
  const isPadel = sport.toLowerCase() === 'pádel';
  
  const formatDiff = (val: number) => val > 0 ? `+${val}` : `${val}`;
  const getDiffColor = (val: number) => val > 0 ? 'text-green-600' : val < 0 ? 'text-red-600' : 'text-muted-foreground';

  const MobilePlayerCard = ({ player }: { player: Player }) => {
    const setsDiff = (player.setsFor || 0) - (player.setsAgainst || 0);
    const gamesDiff = player.difference;
    const matchesPlayed = (player.wins || 0) + (player.losses || 0);
    const points = type === 'round-robin' ? (player.wins || 0) * 3 : player.gamesFor - player.gamesAgainst;
    const isQualifying = !!qualifyingSpots && player.position <= qualifyingSpots;

    return (
      <div
        className={`p-4 rounded-lg border-l-4 shadow-sm transition-all hover:shadow-md ${
          player.isCurrentUser
            ? `border-l-primary ${isQualifying ? 'bg-green-50' : 'bg-gradient-to-r from-primary/5 to-white'} border border-primary/20`
            : isQualifying
              ? 'border-l-green-500 bg-green-50 border border-green-200'
              : 'border-l-gray-300 bg-white border border-gray-200'
        }`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <div className="font-bold text-base text-gray-900">{player.name}</div>
            {(isPadel || modalidad === 'doubles') && player.partner && (
              <div className="font-medium text-sm text-gray-600">{player.partner}</div>
            )}
          </div>
          <div className={`text-3xl font-bold ml-4 ${type === 'round-robin' ? 'text-primary' : getDiffColor(points)}`}>
            {type === 'round-robin' ? points : formatDiff(points)}
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-1">
          {t('tournament.matchesPlayed')}: <span className="font-medium">{matchesPlayed}</span>
          <span className="mx-2">|</span>
          {t('tournament.wins')}: <span className="font-medium text-green-600">{player.wins || 0}</span>
          <span className="mx-2">|</span>
          {t('tournament.losses')}: <span className="font-medium text-red-600">{player.losses || 0}</span>
        </div>

        <div className="text-sm text-gray-600 mb-2">
          {type === 'round-robin' && (
            <>
              {t('tournament.sets')}: <span className="font-medium">{player.setsFor || 0}-{player.setsAgainst || 0}</span>
              <span className={`ml-1 ${getDiffColor(setsDiff)}`}>({formatDiff(setsDiff)})</span>
              <span className="mx-2">|</span>
            </>
          )}
          {t('tournament.games')}: <span className="font-medium">{player.gamesFor}-{player.gamesAgainst}</span>
          <span className={`ml-1 ${getDiffColor(gamesDiff)}`}>({formatDiff(gamesDiff)})</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-700">{t('tournament.position')}: {player.position}º</span>
          {isQualifying && (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
              Clasifica
            </Badge>
          )}
          {player.isCurrentUser && (
            <Badge className="bg-primary/10 text-primary text-xs">
              {t('league.you')}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className={isMobile ? 'p-4' : 'p-6'}>
        <CardTitle className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900 flex items-center gap-2`}>
          <Trophy className="h-5 w-5 text-amber-500" />
          {t('league.standings')}
        </CardTitle>
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-3 rounded-lg border border-primary/20">
          {type === 'round-robin' ? (
            <>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-primary font-medium`}>
                🏆 {t('league.systemRoundRobin')}
              </p>
                 <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-primary/80 mt-1`}>
                {t('league.tiebreakRoundRobin')}
              </p>
            </>
          ) : (
            <>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-primary font-medium`}>
                🎯 {t('league.systemSumatoria')}
              </p>
              <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-primary/80 mt-1`}>
                {t('league.tiebreakSumatoria')}
              </p>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className={isMobile ? 'p-4 pt-0' : 'p-6 pt-0'}>
        {isMobile ? (
          <div className="space-y-3">
            {players.map((player) => (
              <MobilePlayerCard key={player.position} player={player} />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t('league.pos')}</TableHead>
                  <TableHead>{isPadel ? t('league.pair') : (modalidad === 'doubles' ? t('league.pair') : t('league.player'))}</TableHead>
                   {type === 'round-robin' && (
                     <>
                       <TableHead className="text-center">V</TableHead>
                       <TableHead className="text-center">D</TableHead>
                     </>
                   )}
                   {type === 'round-robin' && (
                     <>
                       <TableHead className="text-center">SF</TableHead>
                       <TableHead className="text-center">SC</TableHead>
                     </>
                   )}
                   <TableHead className="text-center">JF</TableHead>
                   <TableHead className="text-center">JC</TableHead>
                   <TableHead className="text-center">{t('league.difference').substring(0, 3)}</TableHead>
                   {type === 'round-robin' && <TableHead className="text-center">{t('league.points')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {players.map((player) => {
                  const isQualifying = !!qualifyingSpots && player.position <= qualifyingSpots;
                  return (
                  <TableRow 
                    key={player.position}
                    className={`transition-colors hover:bg-gray-50 ${
                      player.isCurrentUser ? 'border-l-4 border-l-primary' : ''
                    } ${
                      isQualifying
                        ? 'bg-green-50'
                        : player.position <= 3
                          ? 'bg-gradient-to-r from-amber-50 to-amber-100'
                          : player.isCurrentUser ? 'bg-primary/5' : ''
                    }`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        {getPositionIcon(player.position)}
                        <span className="text-lg">{player.position}</span>
                        {getPositionBadge(player.position)}
                        {isQualifying && (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
                            Clasifica
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={`font-medium ${player.isCurrentUser ? 'text-primary' : ''}`}>
                      {isPadel || modalidad === 'doubles' ? (
                        <div className="space-y-1">
                          <div>{player.name}</div>
                          <div className="text-sm text-gray-500">{player.partner}</div>
                        </div>
                      ) : (
                        <div>{player.name}</div>
                      )}
                      {player.isCurrentUser && <Badge variant="outline" className="ml-2 text-xs">{t('league.you')}</Badge>}
                    </TableCell>
                     {type === 'round-robin' && (
                       <>
                         <TableCell className="text-center font-medium text-green-600">{player.wins}</TableCell>
                         <TableCell className="text-center font-medium text-red-600">{player.losses}</TableCell>
                       </>
                     )}
                     {type === 'round-robin' && (
                       <>
                         <TableCell className="text-center font-medium">{player.setsFor || 0}</TableCell>
                         <TableCell className="text-center font-medium">{player.setsAgainst || 0}</TableCell>
                       </>
                     )}
                     <TableCell className="text-center font-bold text-primary">{player.gamesFor}</TableCell>
                    <TableCell className="text-center">{player.gamesAgainst}</TableCell>
                    <TableCell className={`text-center font-bold ${
                      player.difference > 0 ? 'text-green-600' : 
                      player.difference < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {player.difference > 0 ? '+' : ''}{player.difference}
                    </TableCell>
                    {type === 'round-robin' && (
                      <TableCell className="text-center font-bold text-primary">
                        {(player.wins || 0) * 3}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeagueRankingTable;
